import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  CircleSlash,
  Loader2,
  Maximize2,
} from '@lognai/synapse-design-system-v2/icons';
import { Button, Skeleton } from '@lognai/synapse-design-system-v2';
import {
  HighchartsRenderer,
  VisualDataTable,
  VisualDetailModal,
  drillAnalysis,
  drillSubtitle,
  executeAnalysis,
  executeVisualBatch,
  formatDrillCrumb,
  formatMetric,
  resolveChartTheme,
  userFacingAnalyticsError,
  type AnalysisResult,
  type BatchResult,
  type ChartThemePayload,
  type DrillSelection,
} from '@lognai/plugin-analytics-ui';
import type { ReportWorkspaceCardContent } from '../../services/api/workspace';
import { chartWorkspaceContent } from './workspace-card-content';
import { useCardHeaderSlot } from './card-header-slot';

/** A saved analytics chart with dashboard drill behavior and the shared detail modal. */
export function WorkspaceChartCard({
  visualId,
  onTitle,
  zeroIsPositive = false,
  presentation = 'chart',
  onContent,
}: {
  visualId: string;
  /** Reports the chart's own title so the card header can show it. */
  onTitle?: (title: string) => void;
  /** True when a zero result is a healthy outcome (risk/exposure metrics). */
  zeroIsPositive?: boolean;
  presentation?: 'chart' | 'table';
  onContent?: ReportWorkspaceCardContent;
}) {
  const headerSlot = useCardHeaderSlot();
  const [execution, setExecution] = useState<BatchResult>();
  const [theme, setTheme] = useState<ChartThemePayload>();
  const [failed, setFailed] = useState(false);

  // Drill state, mirroring the dashboard widget: a stack of prior results plus
  // the crumbs that describe how we got here.
  const [result, setResult] = useState<AnalysisResult>();
  const [history, setHistory] = useState<AnalysisResult[]>([]);
  const [crumbs, setCrumbs] = useState<Array<{ member: string; key: string; label: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const [selection, setSelection] = useState<DrillSelection>();
  const [detailOpen, setDetailOpen] = useState(false);

  // Highcharts needs an explicit pixel height; measuring the region and feeding
  // it in is what makes the chart fit whatever size the card is dragged to.
  const regionRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(220);

  useLayoutEffect(() => {
    const region = regionRef.current;
    if (!region) return;
    const measure = () =>
      setChartHeight(Math.max(120, Math.floor(region.getBoundingClientRect().height - 8)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(region);
    return () => observer.disconnect();
  }, [execution]);

  useEffect(() => {
    let live = true;
    setExecution(undefined);
    setResult(undefined);
    setSelection(undefined);
    setDetailOpen(false);
    setError(undefined);
    setFailed(false);
    setHistory([]);
    setCrumbs([]);
    executeVisualBatch([{ visualId }])
      .then((batch) => {
        if (!live) return;
        const next = batch.results[0];
        if (!next) return setFailed(true);
        setExecution(next);
        setResult(next.result);
        const title = next.result?.visual?.title;
        if (title) onTitle?.(title);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [visualId]);

  useEffect(() => {
    let live = true;
    resolveChartTheme()
      .then((resolved) => {
        if (live) setTheme(resolved.payload);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    onContent?.(`${presentation === 'table' ? 'card' : 'chart'}:${visualId}`, chartWorkspaceContent(result, failed || execution?.ok === false));
  }, [result, failed, execution?.ok, onContent, presentation, visualId]);

  const drill = async (next: DrillSelection) => {
    if (!result) return;
    try {
      setBusy(true);
      setError(undefined);
      const analysis = drillAnalysis(result.analysis, next.member, next.point);
      if (!analysis) return;
      const drilled = await executeAnalysis(analysis);
      setHistory((items) => [...items, result]);
      setCrumbs((items) => [
        ...items,
        { member: next.member, key: next.point.key, label: next.point.label },
      ]);
      setResult(drilled);
      setSelection(next);
    } catch (cause) {
      setError(userFacingAnalyticsError(cause));
    } finally {
      setBusy(false);
    }
  };

  const goToDepth = (depth: number) => {
    if (!history.length || depth >= crumbs.length) return;
    const target = history[depth];
    if (!target) return;
    setResult(target);
    setHistory((items) => items.slice(0, depth));
    setCrumbs((items) => items.slice(0, depth));
    setSelection(undefined);
  };

  const rowState = result?.meta?.rowState;
  const primaryEvidence = result?.evidence?.[0];
  const subtitle =
    crumbs.length && result ? drillSubtitle(crumbs, result.visual.categoryMember) : undefined;

  const centred = (children: React.ReactNode) => (
    <div className="flex h-full min-h-[140px] flex-col items-center justify-center px-6 text-center">
      {children}
    </div>
  );

  const body = () => {
    if (failed || (execution && !execution.ok)) {
      return centred(
        <p className="text-xs text-muted-foreground">This chart could not be loaded.</p>
      );
    }
    if (rowState === 'empty') {
      return centred(
        <>
          <div className="h-1 w-10 rounded-full bg-muted" />
          <p className="mt-3 text-sm font-medium">No data for the current company</p>
          <p className="mt-1 text-xs text-muted-foreground">Try a wider date range in Analytics.</p>
        </>
      );
    }
    if (rowState === 'zero') {
      return centred(
        zeroIsPositive ? (
          <>
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-success-subtle text-success">
              <Check size={18} />
            </div>
            <p className="mt-3 text-sm font-medium">No current exposure detected</p>
          </>
        ) : (
          <>
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <CircleSlash size={18} />
            </div>
            <p className="mt-3 text-sm font-medium">Zero recorded for this period</p>
          </>
        )
      );
    }
    if (result) {
      if (presentation === 'table') return <div className="h-full overflow-auto px-3"><VisualDataTable analysis={result.analysis} visual={result.visual} /></div>;
      return (
        <HighchartsRenderer
          visual={result.visual}
          onSelect={(next) => void drill(next)}
          height={chartHeight}
          compact
          theme={theme}
        />
      );
    }
    return (
      <div
        className="flex h-full min-h-[140px] flex-col justify-end gap-2 p-3"
        aria-busy="true"
        aria-label="Loading chart"
      >
        <div className="flex min-h-0 flex-1 items-end gap-2">
          {[45, 70, 55, 85, 62, 74].map((height, index) => (
            <Skeleton key={index} className="w-full" style={{ height: `${height}%` }} />
          ))}
        </div>
        <Skeleton className="h-3 w-28" />
      </div>
    );
  };

  /**
   * The value and the chart's own controls, rendered onto the card's title line
   * rather than a toolbar of their own — a workspace card is short, and a
   * dedicated row cost the chart height it could not spare.
   */
  const headerControls = (
    <>
      {primaryEvidence && rowState === 'ready' && (
        <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
          {formatMetric(Number(primaryEvidence.value), primaryEvidence.unit ?? 'number')}
        </span>
      )}
      {history.length > 0 && (
        <Button
          variant="ghost"
          size="xs"
          aria-label="Back one drill level"
          title="Back"
          onClick={() => goToDepth(Math.max(0, crumbs.length - 1))}
        >
          <ArrowLeft size={14} />
        </Button>
      )}
      <Button
        variant="outline"
        size="xs"
        aria-label="Enlarge chart"
        title="Enlarge chart"
        disabled={!result}
        onClick={() => setDetailOpen(true)}
      >
        <Maximize2 size={14} />
      </Button>
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {headerSlot ? createPortal(headerControls, headerSlot) : null}

      {crumbs.length > 0 && result && (
        <nav
          className="flex min-h-[30px] shrink-0 items-center gap-1 overflow-x-auto border-y border-border bg-muted/20 px-3 py-1 text-xs"
          aria-label="Chart drilldown"
          title={subtitle}
        >
          <Button
            className="h-6 shrink-0 px-1.5 text-xs"
            variant="ghost"
            size="xs"
            onClick={() => goToDepth(0)}
          >
            All
          </Button>
          {crumbs.map((crumb, index) => (
            <span
              className="inline-flex shrink-0 items-center gap-1"
              key={`${crumb.member}-${index}`}
            >
              <ChevronRight size={12} className="text-muted-foreground" />
              {index < crumbs.length - 1 ? (
                <button
                  className="max-w-[130px] truncate px-1 py-0.5 font-medium hover:text-primary"
                  title={formatDrillCrumb(crumb)}
                  onClick={() => goToDepth(index + 1)}
                >
                  {formatDrillCrumb(crumb)}
                </button>
              ) : (
                <span
                  className="max-w-[130px] truncate px-1 py-0.5 font-medium"
                  title={formatDrillCrumb(crumb)}
                >
                  {formatDrillCrumb(crumb)}
                </span>
              )}
            </span>
          ))}
        </nav>
      )}

      <div ref={regionRef} className="relative min-h-0 flex-1 overflow-hidden px-2">
        {body()}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/75">
            <Loader2 className="animate-spin text-primary" />
          </div>
        )}
      </div>


      {error && (
        <div className="shrink-0 border-t border-destructive-border bg-destructive-subtle px-3 py-1.5 text-xs text-destructive-foreground">
          {error}
        </div>
      )}

      <VisualDetailModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        result={result}
        selection={selection}
        theme={theme}
      />
    </div>
  );
}
